if (!customElements.get('ace-floating-mascots')) {
  customElements.define('ace-floating-mascots', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      this.options = { signal: this.controller.signal };
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.pauseButton = this.querySelector('.ace-floating-mascots__pause');
      this.paused = false;
      this.jumpTimers = new Set();
      this.querySelectorAll('.ace-floating-mascots__walker').forEach(walker => {
        const button = walker.querySelector('.ace-mascot-button');
        let lastJump = -2000;
        const jump = () => {
          const now = performance.now();
          if (walker.hasAttribute('data-jumping') || now - lastJump < 1200 || document.hidden || this.hasAttribute('data-obstructed')) return;
          lastJump = now;
          walker.setAttribute('data-jumping', '');
          const timer = window.setTimeout(() => {
            walker.removeAttribute('data-jumping');
            this.jumpTimers.delete(timer);
          }, 980);
          this.jumpTimers.add(timer);
        };
        button.addEventListener('pointerenter', event => {
          if (event.pointerType === 'mouse' || event.pointerType === 'pen') jump();
        }, this.options);
        button.addEventListener('focus', jump, this.options);
        button.addEventListener('click', jump, this.options);
      });
      this.schedule = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = 0;
          this.measure();
        });
      };
      this.pauseButton.addEventListener('click', () => {
        this.paused = !this.paused;
        this.toggleAttribute('data-paused', this.paused);
        this.pauseButton.setAttribute('aria-pressed', String(this.paused));
        this.pauseButton.setAttribute('aria-label', this.paused ? this.pauseButton.dataset.playLabel : this.pauseButton.dataset.pauseLabel);
        document.documentElement.toggleAttribute('data-ace-peek-paused', this.paused);
        document.dispatchEvent(new CustomEvent('ace:peek-motion-change'));
        this.updateMotion();
      }, this.options);
      this.motion.addEventListener('change', this.schedule, this.options);
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          this.jumpTimers.forEach(timer => window.clearTimeout(timer));
          this.jumpTimers.clear();
          this.querySelectorAll('[data-jumping]').forEach(walker => walker.removeAttribute('data-jumping'));
        }
        this.schedule();
      }, this.options);
      document.addEventListener('focusin', this.schedule, this.options);
      document.addEventListener('focusout', this.schedule, this.options);
      window.addEventListener('resize', this.schedule, this.options);
      window.addEventListener('scroll', this.schedule, { ...this.options, passive: true });
      window.addEventListener('load', this.schedule, this.options);
      window.addEventListener('pageshow', this.schedule, this.options);
      window.visualViewport?.addEventListener('resize', this.schedule, this.options);
      this.observer = new MutationObserver(records => {
        if (records.some(record => !this.contains(record.target) && (record.type === 'childList' || record.target.closest?.('ace-home-jump, details-modal, menu-drawer, cart-drawer, shopify-chat, #PBarNextFrameWrapper')))) this.schedule();
      });
      this.observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['open', 'aria-expanded', 'hidden', 'class'] });
      this.measure();
    }
    updateMotion() {
      this.toggleAttribute('data-running', !this.paused && !this.motion.matches && !document.hidden && !this.hasAttribute('data-obstructed'));
    }
    measure() {
      const vh = document.documentElement.clientHeight;
      const mobile = window.innerWidth < 750;
      const margin = mobile ? 10 : 14;
      const chat = document.querySelector('shopify-chat');
      if (chat?.shadowRoot && this.chatRoot !== chat.shadowRoot) {
        this.chatObserver?.disconnect();
        this.chatRoot = chat.shadowRoot;
        this.chatObserver = new MutationObserver(this.schedule);
        this.chatObserver.observe(this.chatRoot, { childList: true, subtree: true, attributes: true, attributeFilter: ['aria-expanded', 'hidden', 'class'] });
      }
      const activator = this.chatRoot?.querySelector('button[part="activator"]');
      const jump = document.querySelector('.ace-home-jump');
      const actions = jump?.querySelector('.ace-home-jump__actions');
      const preview = document.getElementById('PBarNextFrameWrapper') || document.getElementById('PBarNextFrame');
      let bottom = preview ? 208 : 142;
      for (const control of [activator, actions]) {
        const rect = control?.getBoundingClientRect();
        if (rect && rect.width > 0 && rect.height > 0 && rect.top > vh * .35) bottom = Math.max(bottom, vh - rect.top + margin);
      }
      this.style.setProperty('--ace-floating-bottom', `${Math.round(bottom)}px`);
      const focusedInput = document.activeElement?.matches('input,textarea,select,[contenteditable="true"]');
      const menuOpen = jump?.querySelector('[data-jump-toggle]')?.getAttribute('aria-expanded') === 'true';
      const chatOpen = activator?.getAttribute('aria-expanded') === 'true';
      const overlayOpen = document.querySelector('menu-drawer details[open], details-modal details[open], cart-drawer.active, dialog[open]');
      const rect = this.getBoundingClientRect();
      const topSafe = (parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--ace-sticky-announcement-height')) || 0) + (document.querySelector('.section-header')?.offsetHeight || 0) + 12;
      const hopHeight = Math.max(0, Math.min(mobile ? 22 : 30, rect.top - topSafe - 30));
      this.style.setProperty('--ace-floating-hop-height', `${Math.round(hopHeight)}px`);
      let obstructed = Boolean(menuOpen || chatOpen || overlayOpen || (mobile && focusedInput) || rect.top < topSafe + 30);
      if (mobile && !obstructed) {
        obstructed = Array.from(document.querySelectorAll('#MainContent a.button, #MainContent button[type="submit"], #MainContent input, #MainContent select, #MainContent textarea')).some(control => {
          const box = control.getBoundingClientRect();
          return box.width > 0 && box.height > 0 && box.left < rect.right && box.right > rect.left && box.top < rect.bottom && box.bottom > rect.top;
        });
      }
      this.toggleAttribute('data-obstructed', obstructed);
      this.updateMotion();
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      this.chatObserver?.disconnect();
      this.jumpTimers?.forEach(timer => window.clearTimeout(timer));
      this.jumpTimers?.clear();
      cancelAnimationFrame(this.frame);
    }
  });
}

